import type { Grade } from '../../core/types';
import { useCountUp } from '../../hooks/useCountUp';
import { cn } from '../../lib/cn';
import { scoreTone } from '../../lib/tone';

export interface ScoreChipProps {
  score: number;
  grade: Grade;
}

const SIZE = 20;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The score, shrunk to fit in the header.
 *
 * This exists for the phone. On a wide screen the real `ScoreRing` is pinned
 * beside the form and the number is never out of sight, which is the whole
 * feedback loop this tool is built around. On a 375px screen the report sits
 * below the entire form — measured at five screens down — so the number the app
 * is *for* was invisible for the whole time someone was typing. A chip in a
 * sticky bar puts it back on screen at every scroll position, at the cost of
 * 90px of header.
 *
 * It is a link to the full report rather than a readout, because a number you
 * cannot get to the reasons behind is just a grade.
 *
 * Two details it shares with `ScoreRing` on purpose: the count-up, so both
 * arrive at a new score together, and `scoreTone()`, so the ring here is the
 * same hue as the big one rather than a second opinion. Three it deliberately
 * does *not* share: the grade letter, which the ring already stickers and which
 * would make this three things in 70px; the literal words "out of 100", which
 * live in the ring alone — `aria-label` carries them here so a screen reader
 * still hears a scale rather than a bare digit; and the display face, which the
 * ring gets at 60px and this cannot justify at 14px.
 */
export function ScoreChip({ score, grade }: ScoreChipProps) {
  const clamped = Math.max(0, Math.min(100, score));
  const tone = scoreTone(clamped);
  const counted = useCountUp(clamped);

  return (
    <a
      href="#report"
      aria-label={`Overall score ${clamped} of 100, grade ${grade}. Jump to the full report.`}
      className="flex shrink-0 items-center gap-1.5 rounded-pill border border-line bg-card py-1 pr-2.5 pl-1.5 no-underline transition-colors hover:border-brand-300 hover:bg-sunken dark:hover:border-brand-500/60"
    >
      <svg
        aria-hidden="true"
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        // Same 12 o'clock start as the big ring.
        className="-rotate-90"
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="var(--ui-line)"
          strokeWidth={STROKE}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={tone.stroke}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - clamped / 100)}
          style={{
            transition: 'stroke-dashoffset 0.7s var(--ease-out-quint), stroke 0.4s linear',
          }}
        />
      </svg>
      {/* Body face, not the display one, and `tabular-nums`. The face is the
          rule `global.css` states outright — Space Grotesk is a distraction at
          14px — which also keeps this number in the same face as the section
          pills a bar below it and the section bars in the report. The figures
          matter because this number changes under the reader's eyes, and a
          proportional 1 would make the whole chip twitch sideways on the way
          from 19 to 20. */}
      <span className={cn('text-sm font-bold tabular-nums', tone.text)}>{counted}</span>
    </a>
  );
}
